import { useId, useRef, useState } from "react";
import { useEditor, useEditorState, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontSize } from "@tiptap/extension-text-style/font-size";
import { ResizableImage } from "./ResizableImage";
import { Placeholder } from "@tiptap/extensions";

const FONT_SIZES = [
  { label: "Small", value: "0.85em" },
  { label: "Normal", value: "" },
  { label: "Large", value: "1.25em" },
  { label: "X-Large", value: "1.6em" },
];

function ToolbarButton({ onClick, active, children, title, disabled }) {
  return (
    <button type="button" onClick={onClick} title={title} aria-label={title} aria-pressed={active === undefined ? undefined : active} disabled={disabled}
      className={`px-2.5 py-1.5 rounded-md text-sm font-bold border-none cursor-pointer disabled:opacity-40 disabled:cursor-default ${
        active ? "bg-green-action-light text-white" : "bg-dark-800 text-gray-300 hover:bg-dark-600"
      }`}>
      {children}
    </button>
  );
}

// A small Tiptap-based rich text editor for the event HOME page welcome message and
// the fighter welcome email body. Content is stored/emitted as sanitized-on-render
// HTML (see utils/sanitizeHtml.js) - this component itself does not sanitize, since it
// only ever writes to the admin's own event doc, not directly to the DOM of another
// user.
//
// `onImageUpload`, if provided, enables the image toolbar button: it receives the
// picked File and must resolve to a public URL (see uploadRichContentImage in
// firebase/services.js) - image src has to be a plain fetchable URL, not a data: URI
// or blob:, since the HTML is later reused verbatim in the fighter welcome email sent
// through Resend. Callers that don't pass it (if any ever appear) simply won't get the
// button.
export default function RichTextEditor({ content, onChange, placeholder, onImageUpload, label = "Rich text editor" }) {
  const fileInputRef = useRef(null);
  const imageAltId = useId();
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [imageAlt, setImageAlt] = useState("");
  const [decorative, setDecorative] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit, TextStyle, FontSize, ResizableImage, Placeholder.configure({ placeholder })],
    content: content || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "rich-content min-h-[220px] p-3 outline-none",
        "aria-label": label,
      },
    },
  });

  const handleImageButtonClick = () => fileInputRef.current?.click();

  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file later
    if (file) { setPendingImage(file); setImageAlt(""); setDecorative(false); }
  };

  const insertImage = async (e) => {
    e.preventDefault();
    if (!pendingImage || !editor || (!decorative && !imageAlt.trim())) return;
    setUploadingImage(true);
    try {
      const url = await onImageUpload(pendingImage);
      editor.chain().focus().setImage({ src: url, alt: decorative ? "" : imageAlt.trim() }).run();
      setPendingImage(null);
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to upload image");
    }
    setUploadingImage(false);
  };

  const state = useEditorState({
    editor,
    selector: (ctx) => ({
      bold: ctx.editor?.isActive("bold") ?? false,
      italic: ctx.editor?.isActive("italic") ?? false,
      underline: ctx.editor?.isActive("underline") ?? false,
      bulletList: ctx.editor?.isActive("bulletList") ?? false,
      orderedList: ctx.editor?.isActive("orderedList") ?? false,
      fontSize: ctx.editor?.getAttributes("textStyle")?.fontSize || "",
      imageSelected: ctx.editor?.isActive("image") ?? false,
      imageAlign: ctx.editor?.getAttributes("image")?.align || null,
      selectedImageAlt: ctx.editor?.getAttributes("image")?.alt || "",
    }),
  });

  return (
    <div className="bg-dark-800 border border-dark-500 rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center gap-1.5 p-2 border-b border-dark-500 bg-dark-700">
        <ToolbarButton title="Bold" active={state.bold} onClick={() => editor.chain().focus().toggleBold().run()}>B</ToolbarButton>
        <ToolbarButton title="Italic" active={state.italic} onClick={() => editor.chain().focus().toggleItalic().run()}><em>I</em></ToolbarButton>
        <ToolbarButton title="Underline" active={state.underline} onClick={() => editor.chain().focus().toggleUnderline().run()}><span className="underline">U</span></ToolbarButton>
        <div className="w-px h-5 bg-dark-500 mx-0.5" />
        <ToolbarButton title="Bullet list" active={state.bulletList} onClick={() => editor.chain().focus().toggleBulletList().run()}>• List</ToolbarButton>
        <ToolbarButton title="Numbered list" active={state.orderedList} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. List</ToolbarButton>
        <div className="w-px h-5 bg-dark-500 mx-0.5" />
        <select value={state.fontSize} aria-label="Font size" title="Font size"
          onChange={(e) => {
            const value = e.target.value;
            if (value) editor.chain().focus().setFontSize(value).run();
            else editor.chain().focus().unsetFontSize().run();
          }}
          className="px-2 py-1.5 bg-dark-800 border border-dark-500 rounded-md text-gray-300 text-xs outline-none cursor-pointer">
          {FONT_SIZES.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
        </select>
        <ToolbarButton title="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>Clear</ToolbarButton>
        {state.imageSelected && (
          <>
            <div className="w-px h-5 bg-dark-500 mx-0.5" />
            <ToolbarButton title="Align image left" active={state.imageAlign === "left"}
              onClick={() => editor.chain().focus().updateAttributes("image", { align: "left" }).run()}>⇤</ToolbarButton>
            <ToolbarButton title="Center image" active={state.imageAlign === "center"}
              onClick={() => editor.chain().focus().updateAttributes("image", { align: "center" }).run()}>↔</ToolbarButton>
            <ToolbarButton title="Align image right" active={state.imageAlign === "right"}
              onClick={() => editor.chain().focus().updateAttributes("image", { align: "right" }).run()}>⇥</ToolbarButton>
          </>
        )}
        {onImageUpload && (
          <>
            <div className="w-px h-5 bg-dark-500 mx-0.5" />
            <ToolbarButton title="Insert image" onClick={handleImageButtonClick} disabled={uploadingImage}>
              {uploadingImage ? "Uploading…" : "🖼 Image"}
            </ToolbarButton>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
              aria-label="Choose image to insert"
              onChange={handleImageFileChange} />
          </>
        )}
      </div>
      {state.imageSelected && <div className="p-2 border-b border-dark-500 flex flex-wrap items-center gap-2">
        <label htmlFor={`${imageAltId}-selected`} className="text-sm">Selected image alternative text</label>
        <input id={`${imageAltId}-selected`} value={state.selectedImageAlt} onChange={(e) => editor.commands.updateAttributes("image", { alt: e.target.value })}
          className="flex-1 min-w-40 p-2 bg-dark-900 border border-gray-400 rounded text-sm" />
        <button type="button" onClick={() => editor.commands.updateAttributes("image", { alt: "" })}
          className="p-2 border border-gray-400 rounded text-sm">Mark decorative</button>
      </div>}
      {pendingImage && <form onSubmit={insertImage} className="p-3 border-b border-dark-500 space-y-2">
        <div className="text-sm font-bold">Describe {pendingImage.name}</div>
        <label htmlFor={imageAltId} className="block text-sm">Alternative text</label>
        <input id={imageAltId} value={imageAlt} onChange={(e) => setImageAlt(e.target.value)} disabled={decorative}
          className="w-full p-2 bg-dark-900 border border-gray-400 rounded" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={decorative} onChange={(e) => setDecorative(e.target.checked)} /> Decorative image</label>
        <div className="flex gap-2">
          <button type="submit" disabled={uploadingImage || (!decorative && !imageAlt.trim())} className="px-3 py-2 bg-green-action text-white rounded disabled:opacity-50">{uploadingImage ? "Uploading…" : "Insert image"}</button>
          <button type="button" onClick={() => setPendingImage(null)} className="px-3 py-2 border border-gray-400 rounded">Cancel</button>
        </div>
      </form>}
      <EditorContent editor={editor} />
    </div>
  );
}
